import {
  OverridableQuery,
  UseQueryOptions,
  serialize,
  useQuery,
  restrictApi,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { IErrorApiResponse } from '@wyz/types';
import { ConnectQueryKeys } from '~/connect/api';

type IHydraCollection<T> = {
  totalItems: number;
  member: T[];
};

type IS2SPartner = {
  name: string;
};

type IS2SFlowTypeEndpoint = {
  flow_type: string;
  name: string;
  path: string;
  methods: string[];
};

type Options = {
  partners?: OverridableQuery<
    UseQueryOptions<IHydraCollection<IS2SPartner>, IErrorApiResponse>
  >;
  flowTypes?: OverridableQuery<
    UseQueryOptions<IHydraCollection<IS2SFlowTypeEndpoint>, IErrorApiResponse>
  >;
};

export const useWyzFlow = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callFlowApi } = useApi();
  const { tenantId } = useConfig();

  const partners = useQuery({
    queryKey: ConnectQueryKeys.s2sPartners.base(String(tenantId)),
    queryFn: () =>
      callFlowApi<IHydraCollection<IS2SPartner>>(
        `/api/partners?${serialize({ platformId: tenantId })}`,
      ),
    ...options.partners,
  });

  const flowTypes = useQuery({
    queryKey: ConnectQueryKeys.s2sFlowTypes.base(),
    queryFn: () =>
      callFlowApi<IHydraCollection<IS2SFlowTypeEndpoint>>(
        '/api/flow-types/endpoints',
      ),
    ...options.flowTypes,
  });

  return restrictApi(options, {
    partners,
    flowTypes,
  });
};

export type { IS2SPartner, IS2SFlowTypeEndpoint };
